import '../css/app.scss';
import './script/neto.js';
import { addEvent, appHeight } from './script/utils';

appHeight();
import mobMenu from './script/mobMenu';
import popupCalls from './script/popupCalls';
import sliders from './script/slider';
import stickyHeader from './script/stickyHeader';
import qtyCombo from './script/qtyCombo';
import tabs from './script/tabs';
import accordian from './script/accordian';
import accordianShipping from './script/accordianShipping';
import lcpBanner from './script/lcpBanner';
import sort from './script/sort';
import showMoreFilters from './script/filterShowmore';
import dropdown from './script/dropdowns';
import scrollCart from './script/cartLine';
import sticky from './script/sticky-line';
import textCopy from './script/textCopy';


// ---------------------------------------------------
// PAGE TYPE
// ---------------------------------------------------
const body = document.body;
const isHome    = body.classList.contains('pagetype--home');
const isCat     = body.classList.contains('pagetype--category');
const isProd    = body.classList.contains('pagetype--product');
const isCheckout = body.classList.contains('pagetype--checkout'); 
const isContent = body.classList.contains('pagetype--content');

// ---------------------------------------------------
// SAFARI-SAFE requestIdleCallback (polyfill)
// ---------------------------------------------------
type IdleDeadlineLike = {
    didTimeout: boolean;
    timeRemaining: () => number;
};

const ric: (cb: (deadline: IdleDeadlineLike) => void, opts?: { timeout?: number }) => number =
    window.requestIdleCallback ||
    ((cb: (deadline: IdleDeadlineLike) => void) =>
        window.setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 1));

const cancelRic: (id: number) => void = window.cancelIdleCallback || window.clearTimeout;


// ---------------------------------------------------
// MODULE INTERFACE
// ---------------------------------------------------
interface InitModule {
    init?: () => void;
    name?: string;
}


// ---------------------------------------------------
// MODULE TIERS
// ---------------------------------------------------

// ★ CRITICAL — runs immediately for instant INP
const criticalModules: InitModule[] = [
    accordian,
    stickyHeader,
    sliders,
];

// ★ ABOVE-FOLD
const aboveFoldModules: InitModule[] = [
    { name: "addEvent", init: addEvent },
    { name: "appHeight", init: appHeight },

];

// ★ BELOW-FOLD (after user interacts or idle)
const belowFoldModules: InitModule[] = [
    mobMenu,
    popupCalls,
];

if(isProd) {
    criticalModules.push(qtyCombo)
    criticalModules.push(accordianShipping)
    criticalModules.push(dropdown)
    criticalModules.push(scrollCart)

}

if(isCheckout) {
    criticalModules.push(scrollCart)
}

if(isCat) {
    criticalModules.push(sort)
    criticalModules.push(showMoreFilters)
    criticalModules.push(sticky)
    criticalModules.push(textCopy)
}

if(isHome) {
    belowFoldModules.push(tabs)
    criticalModules.push(lcpBanner)
}

if(isContent) {
criticalModules.push(textCopy)
}



// Assign names for debugging
[...criticalModules, ...aboveFoldModules, ...belowFoldModules].forEach((m) => {
    if (!m.name) m.name = m.init?.name || 'anonymousModule';
});

// ---------------------------------------------------
// MODULE LOADER (chunked + idle safe)
// ---------------------------------------------------
const loadModules = (modules: InitModule[], chunkSize = 3) => {
    let i = 0;

    const runChunk = () => {
        const end = Math.min(i + chunkSize, modules.length);

        for (; i < end; i++) {
            try {
                modules[i].init?.();
            } catch (e) {
                console.warn(e);
            }
        }

        if (i < modules.length) requestAnimationFrame(runChunk);
    };

    // Use ric polyfill (Safari-safe)
    ric(runChunk, { timeout: 1200 });
};

// ---------------------------------------------------
// EXECUTION ORDER (INP OPTIMIZED)
// ---------------------------------------------------

// 1) CRITICAL — immediate
loadModules(criticalModules, 2);

// 2) ABOVE-FOLD — idle soon (Safari-safe)
ric(() => loadModules(aboveFoldModules, 2), { timeout: 300 });

// 3) BELOW-FOLD — after interaction or idle timeout
const triggerBelowFold = () => loadModules(belowFoldModules, 3);

['scroll', 'mousemove', 'keydown', 'touchstart', 'pointerdown'].forEach((evt) => {
    window.addEventListener(evt, triggerBelowFold, { once: true, passive: true });
});

// Fallback if no interaction
const scheduleBelowFold = () => {
    ric(triggerBelowFold, { timeout: 2000 });
};

if (document.readyState === 'complete' || document.readyState === 'interactive') {
    scheduleBelowFold();
} else {
    window.addEventListener('DOMContentLoaded', scheduleBelowFold, { once: true });
}